import { Flex, Badge, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import TableComponent from '../components/ui-components/TableComponent';
import CreateSensor from '../components/ui-components/CreateSensor';
import ViewSensor from '../components/ui-components/ViewSensor';
import SearchFilter from '../components/ui-components/SearchFilter';
import AddSensorPopup from '../components/ui-components/AddSensorPopup';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import ExcelUploadComponent from '../components/ui-components/ExcelUploadComponent';
import { MdOutlineEdit, MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../config/axiosConfig';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../utils/helpers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SensorPage = () => {
  const [sensors, setSensors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 8;

  const columns = [
    { title: '#', key: 'serialNumber', width: '5%', render: (value) => <span>{value}</span> },
    {
      title: 'Sensor Address',
      key: 'sensor_address',
      width: '25%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'ISIN',
      key: 'isin',
      width: '20%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'ISVN',
      key: 'isvn',
      width: '20%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'Status',
      key: 'isActive',
      width: '15%',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, row) => (
        <Flex gap={'2'} align={'center'}>
          <Flex direction={'row'} gap={'1'}>
            <CreateSensor initialValues={row} onSensorCreated={fetchSensors} showToast={showToast} />
            <ViewSensor initialValues={row} onSensorUpdated={fetchSensors} showToast={showToast} />
          </Flex>
        </Flex>
      ),
    },
  ];

  const fetchSensors = useCallback(async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
    try {
      const response = await axiosInstance.get('/sensor/get-sensors', {
        params: {
          token: null,
          pageNumber: page,
          pageSize,
          isSuperAdmin: true,
          searchQuery: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
        },
      });
      if (response.data && response.data.result) {
        const sensorData = response.data.result.sensorList.map((item, index) => ({
          serialNumber: (page - 1) * pageSize + index + 1,
          token: item.token,
          sensor_address: item.sensorAddress,
          isin: item.isin,
          isvn: item.isvn,
          isActive: item.isActive,
        }));
        setSensors(sensorData);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setSensors([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching sensors:', error);
      setSensors([]);
      setTotalCount(0);
      showToast('Failed to fetch sensors', 'error');
    }
  }, [searchQuery, showActiveOnly]);

  const debouncedFetch = useCallback(debounce((page, query, activeOnly) => {
    fetchSensors(page, query, activeOnly);
  }, 300), [fetchSensors]);

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = () => {
    setCurrentPage(1);
  };

  const handleActiveToggle = () => {
    setCurrentPage(1);
  };

  const showToast = (message, type = 'success') => {
    if (type === 'error') {
      toast.error(message);
    } else {
      toast.success(message);
    }
  };

  const handleUploadSuccess = () => {
    // Refresh the sensors list after successful upload
    fetchSensors(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4" gap="3" wrap="wrap">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search sensors (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <Flex gap="3" align="center">
            {/* Excel Upload Component */}
            <ExcelUploadComponent 
              type="sensor" 
              onUploadSuccess={handleUploadSuccess}
              showToast={showToast}
            />
            {/* Create Sensor Button */}
            <CreateSensor onSensorCreated={() => fetchSensors(1)} showToast={showToast} />
          </Flex>
        </Flex>
        <TableComponent dataStructure={sensors} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} sensors
          </Text>
        )}
      </Flex>
    </>
  );
};

export default SensorPage;